// result-verifier-profile-tamper-checks.ts
//
// Helpers that keep controller-owned files — above all
// `.avatar/verification-profile.json`, the rulebook a worker is verified
// against — out of the worker's reach. Split out of
// result-verifier.ts (plan 20260924-0205, 200-line budget); result-verifier.ts
// still decides WHEN each runs (step 5b on the inventory diff; a before/after
// snapshot around the recipe run for audit F3), because the timing is the point.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { PLAN_INDEX_PATH, VERIFICATION_PROFILE_PATH } from "./controller-owned-paths.js";
import { matchesGlob } from "./result-verifier-checks.js";

/**
 * Current bytes of the verification profile, or `undefined` when absent.
 * Audit F3: compared before/after the recipe run so a mid-attempt rewrite is
 * caught even though the inventory diff was taken earlier.
 */
export async function readVerificationProfileBytes(repoDir: string): Promise<string | undefined> {
  try {
    return await readFile(join(repoDir, VERIFICATION_PROFILE_PATH), "utf8");
  } catch {
    return undefined;
  }
}

/**
 * Step 5b. Controller-owned paths are excluded from the scope check — but
 * skipping one silently is only right for the path the exclusion was BUILT
 * for. Measured while writing this: `.claude/state/autopilot-binding.json`
 * appears in the diff of EVERY attempt, because the controller itself writes
 * a fresh binding each time (Bug E is precisely about not reading that as a
 * worker's scope violation). Rejecting on it would block every mission — a
 * first draft of this check did exactly that and turned the suite red, which
 * is how the distinction got measured rather than assumed.
 *
 * Every OTHER owned path is the opposite case: no controller step writes it
 * mid-attempt (the verification profile never; `.avatar/PLAN.md` only on the
 * done-path after verify or at a redirect tick boundary — 06f R1), and
 * `checkProposedScope` makes claiming it impossible. So an edit reaching here
 * means the proposal boundary was bypassed (or the ticket was scoped `**`),
 * and continuing would verify this attempt against what the worker just wrote.
 * The profile is checked FIRST and keeps its P22 reason byte for byte.
 */
const BINDING_DIR_GLOB = ".claude/state/**";
const PROFILE_REASON_TAIL = "worker không được sửa danh sách recipe mà chính nó bị kiểm bằng.";
const OWNED_REASON_TAIL: Readonly<Record<string, string>> = {
  [PLAN_INDEX_PATH]: "mục lục do controller sinh từ ticket; worker không sửa.",
};

export function controllerOwnedEditInDiffReason(
  excludedPaths: readonly string[],
): string | undefined {
  if (excludedPaths.includes(VERIFICATION_PROFILE_PATH)) {
    return `controller_owned_path_modified: ${VERIFICATION_PROFILE_PATH} — ${PROFILE_REASON_TAIL}`;
  }
  const edited = excludedPaths.find((p) => !matchesGlob(BINDING_DIR_GLOB, p));
  if (edited === undefined) return undefined;
  const tail = OWNED_REASON_TAIL[edited] ?? "đường của controller; worker không sửa.";
  return `controller_owned_path_modified: ${edited} — ${tail}`;
}

/**
 * Audit F3: the profile changed WHILE the recipes ran. Checked by the caller
 * before it returns any evidence reason: if a recipe rewrote the rulebook,
 * every verdict computed from that run is suspect — including a "pass"
 * produced by a recipe the worker just wrote for itself.
 */
export function profileChangedDuringRecipesReason(
  before: string | undefined,
  after: string | undefined,
): string | undefined {
  if (before === after) return undefined;
  return (
    `controller_owned_path_modified: ${VERIFICATION_PROFILE_PATH} đổi TRONG lúc chạy recipe — ` +
    PROFILE_REASON_TAIL
  );
}
